import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HiMenu, HiX } from 'react-icons/hi';
import { RxExit } from 'react-icons/rx';
import { useAuth } from 'context/AuthContext';
import { canAccessMinhasIniciativasElbb } from 'services/minhas-iniciativas-elbb-eligibility.service';
import { useIsDateActive } from 'hooks/useControlDate';
import { isAssessorEtapa3 } from 'types/etapa3';
import { PHASE_IDS } from 'utils/constants';
import logoElbb from 'assets/img/2026/logoELBB.png';
import fotoPadraoElbb from 'assets/img/2026/ELBB/new_foto_padrao_bb.png';
import { isBancaUser } from 'utils/auth';
import './style.scss';

interface HeaderElbbProps {
  currentPage?: 'inicio' | 'iniciativas' | 'classificadas-etapa1' | 'minha-jornada' | 'minhas-iniciativas' | 'regulamento' | 'meus-votos' | 'avaliacao-etapa-2' | 'meus-votos-etapa-2' | 'meus-votos-etapa-3' | 'votacao-etapa-3' | 'indicados';
  /** Quando true, destaca (flag) o link "Minha Jornada" no menu. */
  highlightMinhaJornada?: boolean;
}

export function HeaderElbb({ currentPage = 'inicio', highlightMinhaJornada = false }: HeaderElbbProps) {
  const { user, signOut } = useAuth();
  const isEtapaDoisVotacaoAtiva = useIsDateActive(PHASE_IDS.ETAPA_2_PREMIO);
  const isEtapaTresVotacaoAtiva = useIsDateActive(PHASE_IDS.ETAPA_3_PREMIO);
  const isBancaTecnicaElbb =
    user?.extra?.etapa === 2 &&
    (user?.extra?.categories as string[] | undefined)?.includes('ELBB');
  const isBancaTecnicaEtapa2 = isBancaTecnicaElbb && isEtapaDoisVotacaoAtiva;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasMinhasIniciativasAccess, setHasMinhasIniciativasAccess] = useState(false);
  const logoutPath = isBancaUser(user) ? '/login-banca' : '/login-elbb';
  const isAssessor = isAssessorEtapa3(user ?? null);

  useEffect(() => {
    let isMounted = true;

    async function loadMinhasIniciativasAccess() {
      if (!user?._id) {
        if (isMounted) {
          setHasMinhasIniciativasAccess(false);
        }
        return;
      }

      try {
        const canAccess = await canAccessMinhasIniciativasElbb(user._id);
        if (isMounted) {
          setHasMinhasIniciativasAccess(canAccess);
        }
      } catch {
        if (isMounted) {
          setHasMinhasIniciativasAccess(false);
        }
      }
    }

    loadMinhasIniciativasAccess();

    return () => {
      isMounted = false;
    };
  }, [user?._id]);

  const shouldShowMinhasIniciativas =
    hasMinhasIniciativasAccess || currentPage === 'minhas-iniciativas';

  // Não renderizar até que o usuário esteja carregado
  if (!user) return null;

  const hasEtapa1 = Array.isArray(user.detail?.etapa1) && user.detail.etapa1.length > 0;

  const userName =
    user.extra?.nome_guerra ||
    user.detail?.nome_guerra ||
    user.detail?.nome ||
    user.name ||
    'Usuário';

  const userPhoto = user.image?.small?.url || fotoPadraoElbb;
  
  const getUserName = () => {
    return userName;
  };

  const getFirstLetter = () => {
    const name = user.name || 'U';
    return name.charAt(0).toUpperCase();
  };

  const getUserPhoto = () => {
    return userPhoto;
  };

  return (
    <header className="header-elbb">
      <div className="header-elbb__blue-detail" aria-hidden="true"></div>
      <div className="header-elbb__content">
        <div className="header-elbb__brand">
          <button 
            className="header-elbb__menu-btn" 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu-elbb"
          >
            {isMenuOpen ? <HiX size={24} color="white" aria-hidden="true" /> : <HiMenu size={24} color="white" aria-hidden="true" />}
          </button>
          <img src={logoElbb} alt="Prêmio ELBB" className="header-elbb__logo" />
          <span className="header-elbb__brand-text" aria-hidden="true">Prêmio ELBB</span>
        </div>
        <div className="header-elbb__divider" aria-hidden="true"></div>
        <nav className="header-elbb__nav" aria-label="Navegação principal">
          <a 
            href="/home-elbb" 
            className={`header-elbb__link ${
              currentPage === 'inicio' ? 'header-elbb__link--active' : ''
            }`}
            aria-current={currentPage === 'inicio' ? 'page' : undefined}
          >
            Início
          </a>
          <a
            href="/iniciativas-elbb"
            className={`header-elbb__link ${
              currentPage === 'iniciativas' ? 'header-elbb__link--active' : ''
            }`}
            aria-current={currentPage === 'iniciativas' ? 'page' : undefined}
          >
            Iniciativas
          </a>
          <a
            href="/minha-jornada-elbb"
            className={`header-elbb__link ${
              currentPage === 'minha-jornada' ? 'header-elbb__link--active' : ''
            }${highlightMinhaJornada ? ' header-elbb__link--flagged' : ''}`}
            aria-current={currentPage === 'minha-jornada' ? 'page' : undefined}
          >
            Minha Jornada
          </a>
          {hasEtapa1 && (
            <Link
              to="/meus-votos"
              className={`header-elbb__link ${
                currentPage === 'meus-votos' ? 'header-elbb__link--active' : ''
              }`}
              aria-current={currentPage === 'meus-votos' ? 'page' : undefined}
            >
              Seleção Etapa 1
            </Link>
          )}
          {isBancaTecnicaEtapa2 && (
            <a
              href="/votacao-etapa-2"
              className={`header-elbb__link ${
                currentPage === 'avaliacao-etapa-2' ? 'header-elbb__link--active' : ''
              }`}
              aria-current={currentPage === 'avaliacao-etapa-2' ? 'page' : undefined}
            >
              Avaliação Etapa 2
            </a>
          )}
          {isBancaTecnicaElbb && (
            <Link
              to="/meus-votos-etapa-2"
              className={`header-elbb__link ${
                currentPage === 'meus-votos-etapa-2' ? 'header-elbb__link--active' : ''
              }`}
              aria-current={currentPage === 'meus-votos-etapa-2' ? 'page' : undefined}
            >
              Meus votos Etapa 2
            </Link>
          )}
          {isEtapaTresVotacaoAtiva && !isAssessor && (
            <a
              href="/meus-votos-etapa-3"
              className={`header-elbb__link ${
                currentPage === 'meus-votos-etapa-3' ? 'header-elbb__link--active' : ''
              }`}
              aria-current={currentPage === 'meus-votos-etapa-3' ? 'page' : undefined}
            >
              Meus votos Etapa 3
            </a>
          )}
            <a
              href="/listagem-inscricao-elbb"
              className={`header-elbb__link ${
                currentPage === 'minhas-iniciativas' ? 'header-elbb__link--active' : ''
              }`}
              aria-current={currentPage === 'minhas-iniciativas' ? 'page' : undefined}
            >
              Minhas Iniciativas
            </a>
          <a
            href="/regulamento-elbb"
            className={`header-elbb__link ${
              currentPage === 'regulamento' ? 'header-elbb__link--active' : ''
            }`}
            aria-current={currentPage === 'regulamento' ? 'page' : undefined}
          >
            Regulamento
          </a>
        </nav>
        <div className="header-elbb__user">
          <span className="header-elbb__username" tabIndex={0} aria-label={`Usuário: ${getUserName()}`}>{getUserName().toUpperCase()}</span>
          <div className="header-elbb__avatar" role="img" aria-label={`Foto de perfil de ${getUserName()}`}>
            <img
              src={getUserPhoto()}
              alt=""
              onError={(event) => {
                if (event.currentTarget.src !== fotoPadraoElbb) {
                  event.currentTarget.src = fotoPadraoElbb;
                }
              }}
            />
          </div>
          <button 
            className="header-elbb__logout-btn" 
            aria-label="Sair da conta"
            onClick={() => signOut(logoutPath)}
          >
            <RxExit size={24} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        id="mobile-menu-elbb"
        className={`header-elbb__mobile-menu ${isMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        <nav className="header-elbb__mobile-nav-links" aria-label="Navegação mobile">
          <a 
            href="/home-elbb" 
            className={`header-elbb__mobile-link ${currentPage === 'inicio' ? 'active' : ''}`}
            aria-current={currentPage === 'inicio' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Início
          </a>
          <a
            href="/iniciativas-elbb"
            className={`header-elbb__mobile-link ${currentPage === 'iniciativas' ? 'active' : ''}`}
            aria-current={currentPage === 'iniciativas' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Iniciativas
          </a>
          <a
            href="/minha-jornada-elbb"
            className={`header-elbb__mobile-link ${currentPage === 'minha-jornada' ? 'active' : ''}${highlightMinhaJornada ? ' header-elbb__mobile-link--flagged' : ''}`}
            aria-current={currentPage === 'minha-jornada' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Minha Jornada
            {highlightMinhaJornada && (
              <span className="header-elbb__mobile-link-flag">Confira seu extrato!</span>
            )}
          </a>
          {hasEtapa1 && (
            <Link
              to="/meus-votos"
              className={`header-elbb__mobile-link ${currentPage === 'meus-votos' ? 'active' : ''}`}
              aria-current={currentPage === 'meus-votos' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Seleção Etapa 1
            </Link>
          )}
          {isBancaTecnicaEtapa2 && (
            <a
              href="/votacao-etapa-2"
              className={`header-elbb__mobile-link ${currentPage === 'avaliacao-etapa-2' ? 'active' : ''}`}
              aria-current={currentPage === 'avaliacao-etapa-2' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Avaliação Etapa 2
            </a>
          )}
          {isBancaTecnicaElbb && (
            <Link
              to="/meus-votos-etapa-2"
              className={`header-elbb__mobile-link ${currentPage === 'meus-votos-etapa-2' ? 'active' : ''}`}
              aria-current={currentPage === 'meus-votos-etapa-2' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Meus votos Etapa 2
            </Link>
          )}
          {isEtapaTresVotacaoAtiva && !isAssessor && (
            <a
              href="/meus-votos-etapa-3"
              className={`header-elbb__mobile-link ${currentPage === 'meus-votos-etapa-3' ? 'active' : ''}`}
              aria-current={currentPage === 'meus-votos-etapa-3' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Meus votos Etapa 3
            </a>
          )}
          {shouldShowMinhasIniciativas && (
            <a 
              href="/listagem-inscricao-elbb" 
              className={`header-elbb__mobile-link ${currentPage === 'minhas-iniciativas' ? 'active' : ''}`}
              aria-current={currentPage === 'minhas-iniciativas' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Minhas Iniciativas
            </a>
          )}
          <a
            href="/regulamento-elbb"
            className={`header-elbb__mobile-link ${currentPage === 'regulamento' ? 'active' : ''}`}
            aria-current={currentPage === 'regulamento' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Regulamento
          </a>
        </nav>
      </div>
    </header>
  );
}
